import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { smartBatchApi, type CommitPhaseData } from '@/services/api/smartBatch'
import { Check, Clock, AlertCircle } from 'lucide-react'
import { sortLessonsByDateTime } from '@/utils/lessonSort'

export function CommitPhasePage() {
  const { token } = useParams<{ token: string }>()
  const [data, setData] = useState<CommitPhaseData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [timeLeft, setTimeLeft] = useState<string>('')

  useEffect(() => {
    if (!token) return
    smartBatchApi.getCommitPhase(token)
      .then(setData)
      .catch((e: any) => setError(e?.detail || e?.message || 'Link ungültig oder abgelaufen'))
      .finally(() => setLoading(false))
  }, [token])

  useEffect(() => {
    if (!data?.deadline) return
    const interval = setInterval(() => {
      const diff = new Date(data.deadline!).getTime() - Date.now()
      if (diff <= 0) {
        setTimeLeft('Abgelaufen')
        clearInterval(interval)
        return
      }
      const mins = Math.floor(diff / 60000)
      const secs = Math.floor((diff % 60000) / 1000)
      setTimeLeft(`${mins}:${secs.toString().padStart(2, '0')}`)
    }, 1000)
    return () => clearInterval(interval)
  }, [data?.deadline])

  const handleAccept = async () => {
    if (!token) return
    setSubmitting(true)
    setError(null)
    try {
      await smartBatchApi.respondToCommit(token, { accepted: true })
      setSubmitted(true)
    } catch (e: any) {
      setError(e?.detail || 'Fehler beim Bestätigen')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDecline = async () => {
    if (!token) return
    setSubmitting(true)
    setError(null)
    try {
      await smartBatchApi.respondToCommit(token, { accepted: false })
      setSubmitted(true)
    } catch (e: any) {
      setError(e?.detail || 'Fehler')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">Laden...</div>

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">{error}</p>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Check className="w-6 h-6 text-green-600" />
          </div>
          <p className="text-gray-700 font-medium">Antwort gespeichert</p>
          <p className="text-sm text-gray-500 mt-1">Vielen Dank für Ihre Rückmeldung.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-lg mx-auto p-4">
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <div className="px-4 py-3 border-b border-green-200 flex justify-between items-start">
            <div>
              <div className="text-xs uppercase tracking-wider text-green-700 font-semibold">{data?.requesting_teacher_name}</div>
              <div className="text-sm font-semibold text-green-800">✓ Sie sind ausgewählt</div>
              <div className="text-xs text-green-600">Bitte bestätigen Sie die Übernahme</div>
            </div>
            {timeLeft && (
              <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs font-bold text-amber-700">
                <Clock className="w-3 h-3" /> {timeLeft}
              </div>
            )}
          </div>

          <div className="p-4">
            <div className="text-xs text-gray-600 mb-3 p-2 bg-green-50 rounded border-l-2 border-green-400">
              Sie sind für folgende Lektionen ausgewählt. Bitte <strong>definitiv bestätigen</strong>.
            </div>

            <div className="border border-green-300 rounded overflow-hidden mb-3">
              <div className="bg-green-50 px-3 py-2 border-b border-green-200 text-xs font-semibold text-green-700">
                ✓ Ihre Lektionen · {data?.lessons.length} total
              </div>
              <div className="divide-y divide-gray-100">
                {data?.lessons && sortLessonsByDateTime(data.lessons).map((lesson, i) => (
                  <div key={lesson.lesson_request_id} className="flex items-center gap-3 p-2.5">
                    <div className="w-5 h-5 bg-green-100 rounded flex items-center justify-center text-xs font-bold text-green-700">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-800">{lesson.subject_name} · {lesson.klass_name}</div>
                      <div className="text-xs text-gray-500">{lesson.room_name}</div>
                    </div>
                    <div className="text-xs font-semibold text-gray-600 whitespace-nowrap">
                      {lesson.start_time}–{lesson.end_time}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-xs text-gray-600 mb-4 p-2 bg-green-50 rounded border-l-2 border-green-400">
              Mit der definitiven Zusage werden alle Lektionen verbindlich übernommen.
            </div>

            {error && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">{error}</div>}

            <div className="flex gap-2">
              <button onClick={handleDecline} disabled={submitting} className="flex-1 px-3 py-2.5 border border-red-200 text-red-600 rounded text-xs font-semibold hover:bg-red-50 disabled:opacity-50">
                Ablehnen
              </button>
              <button onClick={handleAccept} disabled={submitting} className="flex-1 px-3 py-2.5 bg-green-500 text-white rounded text-xs font-semibold hover:bg-green-600 disabled:opacity-50">
                {submitting ? 'Bestätigen...' : '✓ Definitiv zusagen'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
